import { AlertHit, ALERT_HIT_TIME_TOLERANCE_MS } from './issue-alert-hits';
import {
  ChangeDetectionStrategy, Component, Input, OnChanges,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { SeverityPalette, severityTier } from 'src/app/service-center/severity';
import { IssueLifecycleStates } from '../../models/issue';
import { SnapshotTriggers } from '../../models/metric-snapshot';
import {
  ConfidenceSegmentVm, ConfidenceSeriesVm, ConfidencePointVm, SeverityStepVm,
} from './confidence-series';

// How an Issue got to the number in its header: confidence over the life of the
// Issue, with severity as a ribbon under it.
//
// Drawn as plain SVG. The application carries no chart library and this needs no
// axis engine: one value between 0 and 1 against time, on a fixed grid.
//
// Confidence and severity answer different questions, so they do not share a line.
// Confidence is how sure we are, and it owns the plot. Severity is how bad it is,
// and it runs along the bottom in the house severity colours, where a reader can
// see it change without reading it as a second confidence.
//
// The confidence line stays dark. Alert markers show source names and times on hover.

// What moved the line at this sample: something the fleet reported, or the Issue
// changing state. An aging pass gets no mark. The line already shows where it moved,
// and the run's hover target says which way and over how many checks.
export type DotKind = 'evidence' | 'transition';

interface DotVm { cx: number; cy: number; title: string; kind: DotKind; }
interface AlertDotVm { cx: number; cy: number; title: string; hit: AlertHit; recoveryAtMs?: number; }
interface AlertGroupVm { cx: number; cy: number; title: string; hits: AlertHit[]; }
interface TickVm { x: number; label: string; anchor: string; }
interface RibbonVm { x: number; width: number; fill: string; title: string; }
interface PathVm { d: string; title: string; }

// Each dot tooltip identifies the event and its timestamp.
const DOT_TITLE_KEYS: Record<DotKind, string> = {
  evidence: '@issueChartSampleEvidence',
  transition: '@issueChartSampleTransition',
};

const VIEW_W = 660;
const VIEW_H = 140;
const PAD_L = 34;
const PAD_R = 68;
const PLOT_TOP = 10;
const PLOT_BOTTOM = 96;
const RIBBON_TOP = 106;
const RIBBON_H = 8;
const LABEL_Y = 128;
// One ten-minute aging cycle, plus the existing delivery tolerance.
const ALERT_UPDATE_WINDOW_MS = 10 * 60_000 + ALERT_HIT_TIME_TOLERANCE_MS;
const RECOVERY_MIN_RISE = 0.1;

@Component({
  selector: 'app-confidence-chart',
  templateUrl: './confidence-chart.component.html',
  styleUrls: ['./confidence-chart.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfidenceChartComponent implements OnChanges {
  @Input() series: ConfidenceSeriesVm | null = null;
  @Input() showDots = true;
  @Input() showSeverity = true;
  @Input() alertHits: AlertHit[] = [];

  readonly viewBox = `0 0 ${VIEW_W} ${VIEW_H}`;
  readonly plotLeft = PAD_L;
  readonly plotRight = VIEW_W - PAD_R;
  readonly plotTop = PLOT_TOP;
  readonly plotBottom = PLOT_BOTTOM;
  readonly ribbonTop = RIBBON_TOP;
  readonly ribbonHeight = RIBBON_H;
  readonly labelY = LABEL_Y;

  // The line the lifecycle promotes on. Drawing it is what makes a climb into an
  // Active Issue, or a fade out of one, legible as an event rather than a slope.
  readonly guideY = this.yOf(0.5);

  alertDots: AlertDotVm[] = [];
  alertGroups: AlertGroupVm[] = [];
  linePath = '';
  runs: PathVm[] = [];
  dots: DotVm[] = [];
  ribbon: RibbonVm[] = [];
  xTicks: TickVm[] = [];
  ariaLabel = '';

  constructor(private readonly translate: TranslateService) {}

  ngOnChanges(): void {
    const s = this.series;
    if (!s || s.endMs <= s.startMs) {
      this.alertDots = [];
      this.alertGroups = [];
      this.linePath = '';
      this.runs = [];
      this.dots = [];
      this.ribbon = [];
      this.xTicks = [];
      this.ariaLabel = '';
      return;
    }

    this.linePath = this.pathOf(s, s.segments);
    this.runs = s.segments
      .filter(g => g.sampleCount > 0)
      .map(g => ({ d: this.pathOf(s, [g]), title: this.runTitle(g) }));
    this.dots = s.points
      .map(p => this.dotOf(s, p))
      .filter((dot): dot is DotVm => dot !== null);
    this.alertDots = [];
    const segments = [...s.segments].reverse();
    const rises = s.segments.filter(segment => segment.toConfidence > segment.fromConfidence
      && segment.toMs - segment.fromMs <= ALERT_UPDATE_WINDOW_MS
      && (segment.sampleCount === 1 || (segment.sampleCount === 0 && s.points.some(point => point.lifecycleState !== IssueLifecycleStates.Closed
        && (point.trigger === SnapshotTriggers.ContributionAdded
          || point.trigger === SnapshotTriggers.StateTransition
          || point.trigger === SnapshotTriggers.AgingThresholdCrossed)
        && point.atMs === segment.toMs && point.confidence === segment.toConfidence))))
      .sort((a, b) => a.toMs - b.toMs);
    for (const hit of this.alertHits) {
      if (hit.atMs < s.startMs - ALERT_HIT_TIME_TOLERANCE_MS || hit.atMs > s.endMs + ALERT_HIT_TIME_TOLERANCE_MS) continue;
      const atMs = Math.max(s.startMs, Math.min(s.endMs, hit.atMs));
      // At a shared boundary, use the segment after the transition.
      const segment = segments.find(g => atMs >= g.fromMs && atMs <= g.toMs);
      if (!segment) continue;
      const span = segment.toMs - segment.fromMs;
      const ratio = span > 0 ? (atMs - segment.fromMs) / span : 1;
      const confidence = segment.fromConfidence + ratio * (segment.toConfidence - segment.fromConfidence);
      // Use the first nearby update, never a later peak or a long compressed run.
      const rise = rises.find(update => update.toMs >= hit.atMs
        && update.toMs - hit.atMs <= ALERT_UPDATE_WINDOW_MS
        && update.fromMs >= hit.atMs - ALERT_HIT_TIME_TOLERANCE_MS
        && update.toConfidence > confidence
        && !s.points.some(point => point.lifecycleState === IssueLifecycleStates.Closed
          && point.atMs >= hit.atMs && point.atMs <= update.toMs));
      const recovery = rise ?? rises.find(update => update.toMs === atMs);
      this.alertDots.push({ hit, cx: this.r(this.xOf(s, rise?.toMs ?? atMs)),
        cy: this.r(this.yOf(rise?.toConfidence ?? confidence)),
        recoveryAtMs: recovery && recovery.toConfidence - recovery.fromConfidence >= RECOVERY_MIN_RISE
          ? recovery.toMs : undefined,
        title: this.translate.instant('@issueChartAlertHit', { subject: hit.subject, time: this.stamp(hit.atMs) }) });
    }
    this.alertGroups = this.groupAlertDots();
    this.ribbon = s.severitySteps.map(step => this.ribbonOf(s, step));
    this.xTicks = this.ticksOf(s);
    this.ariaLabel = this.translate.instant('@issueChartAria', {
      start: Math.round(s.startConfidence * 100),
      end: Math.round(s.endConfidence * 100),
    });
  }

  private groupAlertDots(): AlertGroupVm[] {
    const groups: AlertDotVm[][] = [];
    const canMerge = (a: AlertDotVm[], b: AlertDotVm[]) =>
      new Set([...a, ...b].filter(dot => dot.recoveryAtMs !== undefined).map(dot => dot.recoveryAtMs)).size <= 1;
    for (const dot of [...this.alertDots].sort((a, b) => a.cx - b.cx || a.cy - b.cy)) {
      // Bound the whole group so a chain of nearby dots cannot absorb the full timeline.
      const group = groups.find(members => canMerge(members, [dot]) && members.every(member =>
        Math.hypot(dot.cx - member.cx, dot.cy - member.cy) < 24));
      if (group) group.push(dot);
      else groups.push([dot]);
    }
    // A recovery stays at the top of its rise. Other groups use the latest detection.
    const markerOf = (members: AlertDotVm[]) => members.find(dot => dot.recoveryAtMs !== undefined)
      ?? members.reduce((latest, dot) => dot.hit.atMs > latest.hit.atMs ? dot : latest);
    // Merge nearby targets while each distinct recovery keeps its marker.
    let merged = true;
    while (merged) {
      merged = false;
      for (let i = 0; i < groups.length && !merged; i++) {
        for (let j = i + 1; j < groups.length; j++) {
          if (!canMerge(groups[i], groups[j])) continue;
          const a = markerOf(groups[i]);
          const b = markerOf(groups[j]);
          if (Math.hypot(a.cx - b.cx, a.cy - b.cy) >= 24) continue;
          groups[i].push(...groups[j]);
          groups.splice(j, 1);
          merged = true;
          break;
        }
      }
    }
    return groups.map(members => {
      const marker = markerOf(members);
      const hits = members.map(dot => dot.hit).sort((a, b) => a.atMs - b.atMs);
      const title = members.length === 1 ? marker.title
        : this.translate.instant('@issueChartDetectionGroup', { count: hits.length })
          + '\n' + hits.map(hit => `${hit.subject} · ${this.stamp(hit.atMs)}`).join('\n');
      return { cx: marker.cx, cy: marker.cy, hits, title };
    });
  }

  private xOf(series: ConfidenceSeriesVm, ms: number): number {
    const span = series.endMs - series.startMs;
    const ratio = span > 0 ? (ms - series.startMs) / span : 0;
    const clamped = Math.min(1, Math.max(0, ratio));
    return PAD_L + clamped * (VIEW_W - PAD_L - PAD_R);
  }

  private yOf(confidence: number): number {
    return PLOT_TOP + (1 - Math.min(1, Math.max(0, confidence))) * (PLOT_BOTTOM - PLOT_TOP);
  }

  private pathOf(series: ConfidenceSeriesVm, segments: ConfidenceSegmentVm[]): string {
    if (!segments.length) return '';
    const head = segments[0];
    const parts = [`M ${this.r(this.xOf(series, head.fromMs))} ${this.r(this.yOf(head.fromConfidence))}`];
    for (const g of segments) {
      parts.push(`L ${this.r(this.xOf(series, g.toMs))} ${this.r(this.yOf(g.toConfidence))}`);
    }
    return parts.join(' ');
  }

  // A contribution row is any evidence arriving or being restated, whatever reported
  // it. Everything that is not evidence and not a state change is one of our own
  // passes, or a trigger this client does not know. Neither earns a mark.
  private kindOf(trigger: string): DotKind | null {
    if (trigger === SnapshotTriggers.StateTransition) return 'transition';
    if (trigger === SnapshotTriggers.ContributionAdded) return 'evidence';
    return null;
  }

  private dotOf(series: ConfidenceSeriesVm, point: ConfidencePointVm): DotVm | null {
    const kind = this.kindOf(point.trigger);
    if (!kind) return null;
    return {
      cx: this.r(this.xOf(series, point.atMs)),
      cy: this.r(this.yOf(point.confidence)),
      kind,
      title: this.translate.instant(DOT_TITLE_KEYS[kind], {
        pct: Math.round(point.confidence * 100),
        time: this.stamp(point.atMs),
      }),
    };
  }

  private ribbonOf(series: ConfidenceSeriesVm, step: SeverityStepVm): RibbonVm {
    const x = this.xOf(series, step.fromMs);
    return {
      x: this.r(x),
      width: this.r(Math.max(1, this.xOf(series, step.toMs) - x)),
      fill: this.severityFill(step.severity),
      title: this.translate.instant('@issueChartSeverity', { severity: step.severity }),
    };
  }

  private severityFill(severity: number): string {
    switch (severityTier(severity)) {
      case 4: return SeverityPalette.critical;
      case 3: return SeverityPalette.high;
      case 2: return SeverityPalette.medium;
      case 1: return SeverityPalette.low;
      default: return SeverityPalette.none;
    }
  }

  // An aging run climbs while repeat fires keep the evidence fresh and fades once
  // they stop, so the direction is the part a reader needs from the hover.
  private runTitle(segment: ConfidenceSegmentVm): string {
    const from = Math.round(segment.fromConfidence * 100);
    const to = Math.round(segment.toConfidence * 100);
    const key = to > from ? '@issueChartRunClimb'
      : to < from ? '@issueChartRunFade'
      : '@issueChartRunHeld';
    return this.translate.instant(key, { from, to, count: segment.sampleCount });
  }

  private ticksOf(series: ConfidenceSeriesVm): TickVm[] {
    const mid = series.startMs + (series.endMs - series.startMs) / 2;
    const anchors = ['start', 'middle', 'end'];
    return [series.startMs, mid, series.endMs].map((ms, i) => ({
      x: this.r(this.xOf(series, ms)),
      label: this.axisLabel(series, ms),
      anchor: anchors[i],
    }));
  }

  // A day of history reads by the clock; anything longer reads by the calendar.
  private axisLabel(series: ConfidenceSeriesVm, ms: number): string {
    const lang = this.translate.currentLang || 'en-US';
    const span = series.endMs - series.startMs;
    return formatDate(new Date(ms), span <= 36 * 3600000 ? 'shortTime' : 'MMM d', lang);
  }

  private stamp(ms: number): string {
    return formatDate(new Date(ms), 'short', this.translate.currentLang || 'en-US');
  }

  // SVG coordinates carry no meaning past a tenth of a unit, and the full float
  // makes the rendered markup unreadable when something needs looking at.
  private r(value: number): number {
    return Math.round(value * 10) / 10;
  }
}
